import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useAuth } from "../../../utils/AuthContext";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../../PageHeading";
import ExportOptions from "../../ExportOptions";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaCheck, FaEye } from "react-icons/fa";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { PrintLayout } from "../../PrintLayout";
import logo from "../../../../src/assets/logo/logo-black.png";
import TableSkeleton from "../../TableSkeleton";

const AppointmentList = () => {
  const { user } = useAuth();
  console.log(user);
  
  const [appointments, setAppointments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const itemsPerPage = 5;
  const modalContentRef = useRef(null);
  const API_KEY =
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const BASE_URL = "https://api.muktihospital.com/api";

  useEffect(() => {
    if (user && user.id) {
      fetchUserAppointments(user.id);
    } else {
      console.warn("No user or user ID found");
      toast.error("Please log in to view your appointments.");
    }
  }, [user]);

  useEffect(() => {
  const filteredAppointments = appointments.filter((item) => {
    const searchLower = search.trim().toLowerCase();
    const appointmentId = item.id?.toString().toLowerCase() || "";
    const date = new Date(item.appointmentDate).toLocaleDateString();
    return (
      appointmentId.includes(searchLower) ||
      date.includes(searchLower)
    );
  });
  setFiltered(filteredAppointments);
  setCurrentPage(1);
}, [appointments, search]);

  const fetchUserAppointments = async (userId) => {
  setIsLoading(true); // Set loading true
  try {
    const response = await axios.get(
      `${BASE_URL}/appointment/user/${userId}`,
      {
        headers: { "x-api-key": API_KEY },
      }
    );
    console.log("appointments data", response)
    if (response.data && response.data.data) {
      setAppointments(response.data.data);
    } else {
      setAppointments([]);
      console.warn("No appointments found in response:", response.data);
    }
  } catch (error) {
    console.error("Error fetching user appointments:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      config: error.config,
    });
    if (error.response?.status === 404) {
      setAppointments([]);
    } else if (error.response?.status === 401) {
      toast.error("Unauthorized: Invalid API key or authentication.");
    } else if (error.response?.status === 403) {
      toast.error("Forbidden: Access denied.");
    } else {
      toast.error(error.response?.data?.error || "Failed to load your appointments.");
    }
  } finally {
    setIsLoading(false);
  }
};

  const filteredList = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredList.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (callId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(callId)
        ? prevSelectedRows.filter((id) => id !== callId)
        : [...prevSelectedRows, callId]
    );
  };

  const handleViewClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);
  };

  const handlePrint = () => {
    if (!selectedAppointment) {
      toast.error("No appointment selected for printing.");
      return;
    }
    const printElement = document.querySelector('.print-appointment-slip');
    if (!printElement) {
      toast.error("Print content not found.");
      return;
    }
    setTimeout(() => {
      window.print();
    }, 100); // Added delay to ensure DOM rendering
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "No.",
    "Appointment ID",
    "Scheduled Date",
    "Patient Name",
    "Doctor Name",
    "Status",
  ];

  const dataMapper = (() => {
  let counter = 0;
  return (item) => {
    counter += 1;
    return [
      counter,
      item.id || "",
      new Date(item.appointmentDate).toLocaleDateString() || "",
      item.patientName || "",
      item.doctorName || "",
      item.status || "",
    ].join("\t");
  };
})();

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Appointment"},
    { label: "My Appointments" },
  ];

  const skeletonColumns = [
    { width: 16, height: 16, className: "p-3", skeletonClassName: "ml-3" }, // Checkbox
    { width: 40, height: 14, className: "p-3" }, // No.
    { width: 80, height: 14, className: "p-3" }, // Appointment ID
    { width: 100, height: 14, className: "p-3" }, // Scheduled Date
    { width: 150, height: 14, className: "p-3" }, // Patient Name
    { width: 150, height: 14, className: "p-3" }, // Doctor Name
    { width: 80, height: 14, className: "p-3" }, // Status
    { width: 60, height: 28, className: "p-3 text-center", skeletonClassName: "inline-block" }, // Actions
  ];

  return (
    <div>
      <ToastContainer />
      <style>
        {`
          @media print {
            .print-appointment-slip {
              display: block !important;
              visibility: visible !important;
            }
          }
        `}
      </style>
      <div className="print-hidden">
        <PageHeading title="My Appointments" breadcrumbs={breadcrumbs} />
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Appointment List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by ID or date"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <ExportOptions
                data={filtered}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Appointment List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto print-wrapper">
          <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
            <thead className="bg-gray-50">
              <tr className="bg-gray-100">
                <th className="text-left print-hidden-in-print">
                  <div className="relative left-3 -top-[2px]">
                    <input
                      type="checkbox"
                      id="agreement"
                      className="hidden peer print-hidden"
                      onChange={handleSelectAll}
                      checked={
                        selectedRows.length === filteredList.length &&
                        filteredList.length > 0
                      }
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="agreement"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                    ></label>
                  </div>
                </th>
                {headers.map((header, i) => (
                  <th
                    key={i}
                    className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                  >
                    {header}
                  </th>
                ))}
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                  <TableSkeleton columns={skeletonColumns} rowCount={itemsPerPage} />
                ) : filteredList.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={8}
                      className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                    >
                      No Appointments Found.
                    </td>
                  </tr>
                ) : (
                filteredList.map((item, i) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={`call-${i}`}
                          className="hidden peer print-hidden"
                          checked={selectedRows.includes(item.id)}
                          onChange={() => handleRowSelect(item.id)}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={`call-${i}`}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                        ></label>
                      </div>
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {(currentPage - 1) * itemsPerPage + i + 1}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {item.id || "N/A"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {new Date(item.appointmentDate).toLocaleDateString()}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {item.patientName || "N/A"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {item.doctorName || "N/A"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          item.status === "cancelled"
                            ? "bg-red-100 text-red-600"
                            : item.status === "approved"
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-600"
                        }`}
                      >
                        {item.status || "N/A"}
                      </span>
                    </td>
                    <td className="p-3 text-center space-x-2 print-hidden-in-print flex shrink-0">
                      <button
                        onClick={() => handleViewClick(item)}
                        className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                        title="View Appointment"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && selectedAppointment && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md border-t-4 border-M-Green-color">
              <div ref={modalContentRef} className="modal-content">
                <div className="text-center mb-4">
                  <img
                    src={logo}
                    alt="Mukti Hospital Logo"
                    className="w-64 mx-auto mb-2"
                  />
                  <p className="text-md text-gray-500">Appointment Slip</p>
                  <h6 className="text-md text-gray-800">
                    Appointment Id:{" "}
                    <span className="text-M-text-color font-bold">
                      {selectedAppointment?.id}
                    </span>
                  </h6>
                  <p className="text-md text-gray-800">
                    Appointment Date:{" "}
                    <span className="text-M-text-color font-bold">
                      {new Date(
                        selectedAppointment.appointmentDate
                      ).toLocaleDateString()}
                    </span>
                  </p>
                  <p className="text-md text-gray-800">
                    Serial No:{" "}
                    <span className="text-M-text-color font-bold">
                      {selectedAppointment.serialNumber || "In Progress..."}
                    </span>
                  </p>
                </div>
                <div className="border-t pt-4">
                  <p className="text-gray-600 mb-1">
                    <strong className="font-semibold text-black mr-1">
                      Patient Name:
                    </strong>{" "}
                    {selectedAppointment.patientName}
                  </p>
                  <p className="text-gray-600 mb-1">
                    <strong className="font-semibold text-black mr-1">
                      Patient Mobile:
                    </strong>{" "}
                    {selectedAppointment.mobileNumber}
                  </p>
                  <p className="text-gray-600 mb-1">
                    <strong className="font-semibold text-black mr-1">
                      Patient Age (Years):
                    </strong>{" "}
                    {selectedAppointment.age || "N/A"}
                  </p>
                  <p className="text-gray-600 mb-1">
                    <strong className="font-semibold text-black mr-1">
                      Patient Weight (Kg):
                    </strong>{" "}
                    {selectedAppointment.weight || "N/A"}
                  </p>
                  <p className="text-gray-600">
                    <strong className="font-semibold text-black mr-1">
                      Doctor Name:
                    </strong>{" "}
                    {selectedAppointment.doctorName}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handlePrint}
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-300"
                >
                  Print
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedAppointment && (
          <PrintLayout
            className="print-appointment-slip"
            title="Appointment Slip"
            billInfo={
              <>
                <div>
                  <p>
                    <strong>Appointment ID:</strong> {selectedAppointment.id}
                  </p>
                  <p>
                    <strong>Appointment Date:</strong>{" "}
                    {new Date(selectedAppointment.appointmentDate).toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Serial No:</strong>{" "}
                    {selectedAppointment.serialNumber || "In Progress..."}
                  </p>
                  <p>
                    <strong>Doctor Name:</strong> {selectedAppointment.doctorName}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Patient Name:</strong> {selectedAppointment.patientName}
                  </p>
                  <p>
                    <strong>Patient Mobile:</strong> {selectedAppointment.mobileNumber}
                  </p>
                  <p>
                    <strong>Patient Age (Years):</strong> {selectedAppointment.age || "N/A"}
                  </p>
                  <p>
                    <strong>Patient Weight (Kg):</strong> {selectedAppointment.weight || "N/A"}
                  </p>
                </div>
              </>
            }
          />
        )}

        {totalPages > 1 && (
          <div className="p-5 text-end">
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
              <li>
                <button
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
              </li>
              {totalPages <= 10
                ? [...Array(totalPages)].map((_, i) => (
                    <li key={i}>
                      <button
                        className={`table-pagination-item ${
                          currentPage === i + 1
                            ? "bg-M-primary-color text-white"
                            : ""
                        }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))
                : (() => {
                    const pages = [];
                    const firstPages = 5;
                    const lastPages = 3;

                    for (
                      let i = 1;
                      i <= Math.min(firstPages, totalPages);
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    if (totalPages > firstPages + lastPages) {
                      pages.push(
                        <li key="ellipsis" className="table-pagination-item">
                          ...
                        </li>
                      );
                    }

                    for (
                      let i = Math.max(
                        firstPages + 1,
                        totalPages - lastPages + 1
                      );
                      i <= totalPages;
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}
              <li>
                <button
                  className="table-pagination-item"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <MdKeyboardArrowRight />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentList;